import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  assignUnansweredQuestions,
  fetchEmployees,
  fetchUnansweredQuestions,
  humanError,
} from '../api';
import { Icon } from '../components/icons';
import { WorkspaceShell } from '../components/WorkspaceShell';
import { withAdminOnly } from '../components/withAdminOnly';
import { BLOCK_LABEL } from '../model';
import type { Employee, UnansweredQuestion, UnansweredQuestions } from '../model';
import { pushNotice } from '../notices';
import type { WorkspaceNotice } from '../notices';
import { toggleId } from '../selectionStore';

const PAGE_SIZE = 50;

const employeeDetails = (employee: Employee): string => (
  [employee.position, employee.department].filter(Boolean).join(' · ')
);

const UnansweredQuestionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeeState, setEmployeeState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [employeeQuery, setEmployeeQuery] = useState('');
  const [employeeId, setEmployeeId] = useState<number | null>(null);
  const [payload, setPayload] = useState<UnansweredQuestions | null>(null);
  const [questionState, setQuestionState] = useState<'idle' | 'loading' | 'ready' | 'failed'>('idle');
  const [questionQuery, setQuestionQuery] = useState('');
  const [selected, setSelected] = useState<number[]>([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [title, setTitle] = useState('');
  const [notice, setNotice] = useState<WorkspaceNotice | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const requestRef = useRef(0);

  const loadEmployees = async () => {
    setEmployeeState('loading');
    try {
      setEmployees(await fetchEmployees());
      setEmployeeState('ready');
    } catch {
      setEmployeeState('failed');
    }
  };

  useEffect(() => {
    void loadEmployees();
  }, []);

  const chooseEmployee = async (employee: Employee) => {
    const request = ++requestRef.current;
    setEmployeeId(employee.id);
    setPayload(null);
    setQuestionState('loading');
    setQuestionQuery('');
    setSelected([]);
    setVisibleCount(PAGE_SIZE);
    setTitle(`Непройденные вопросы — ${employee.full_name}`);
    setNotice(null);
    try {
      const data = await fetchUnansweredQuestions(employee.id);
      if (request !== requestRef.current) return;
      setPayload(data);
      setQuestionState('ready');
    } catch {
      if (request === requestRef.current) setQuestionState('failed');
    }
  };

  const employee = employees.find((entry) => entry.id === employeeId) ?? null;
  const employeeNeedle = employeeQuery.trim().toLocaleLowerCase('ru');
  const shownEmployees = employees.filter((entry) => (
    !employeeNeedle
    || [entry.full_name, entry.position, entry.department]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase('ru')
      .includes(employeeNeedle)
  ));

  const filteredQuestions = useMemo(() => {
    const needle = questionQuery.trim().toLocaleLowerCase('ru');
    return (payload?.questions ?? []).filter((question) => (
      !needle
      || [question.text, question.source_test_title, question.source_test_subgroup, question.block]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('ru')
        .includes(needle)
    ));
  }, [payload, questionQuery]);
  const visibleQuestions = filteredQuestions.slice(0, visibleCount);
  const assignableFiltered = filteredQuestions.filter((question) => !question.already_assigned);
  const selectedSet = new Set(selected);
  const allFilteredSelected = assignableFiltered.length > 0
    && assignableFiltered.every((question) => selectedSet.has(question.question_id));

  const toggle = (question: UnansweredQuestion) => {
    if (question.already_assigned || busy) return;
    setSelected((current) => toggleId(current, question.question_id));
  };

  const toggleAllFiltered = () => {
    const ids = assignableFiltered.map((question) => question.question_id);
    if (allFilteredSelected) {
      const remove = new Set(ids);
      setSelected((current) => current.filter((id) => !remove.has(id)));
    } else {
      setSelected((current) => Array.from(new Set([...current, ...ids])));
    }
  };

  const submit = async () => {
    if (!employee || busyRef.current || selected.length === 0 || !title.trim()) return;
    busyRef.current = true;
    setBusy(true);
    setNotice(null);
    try {
      const result = await assignUnansweredQuestions(employee.id, selected, title.trim());
      const created = result.created[0];
      pushNotice({
        kind: 'ok',
        text: `Тест «${created?.test_title ?? title.trim()}» создан и выдан сотруднику ${employee.full_name}: ${result.questions_assigned} вопросов.`,
      });
      navigate(created ? `/admin/tests/${created.test_id}` : '/admin/tests');
    } catch (error) {
      setNotice({
        kind: 'info',
        text: humanError(
          error,
          'Не получилось создать тест. Список мог измениться: обновите вопросы и попробуйте ещё раз.',
        ),
      });
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const basket = employee && payload && payload.available_to_assign > 0 ? (
    <div className="tw-basket">
      <div className="tw-basket-in">
        <span className="tw-basket-count">
          {selected.length > 0 ? `Выбрано вопросов: ${selected.length}` : 'Пока не выбрали вопросы'}
        </span>
        <span className="tw-basket-actions">
          <button type="button" className="tw-btn" onClick={() => navigate('/admin/tests')}>Отмена</button>
          <button
            type="button"
            className="tw-btn tw-btn-primary"
            disabled={selected.length === 0 || !title.trim() || busy}
            onClick={() => void submit()}
          >
            <Icon name="check" />
            Создать и назначить
          </button>
        </span>
        {selected.length === 0 && <span className="tw-help tw-basket-hint">Отметьте хотя бы один вопрос.</span>}
        {selected.length > 0 && !title.trim() && <span className="tw-help tw-basket-hint">Введите название теста.</span>}
      </div>
    </div>
  ) : null;

  return (
    <WorkspaceShell notice={notice} onCloseNotice={() => setNotice(null)} basket={basket}>
      <section className="tw-screen">
        <div className="tw-row">
          <button type="button" className="tw-btn tw-btn-quiet" onClick={() => navigate('/admin/tests')}>
            <Icon name="left" />
            Все тесты
          </button>
        </div>
        <div>
          <h1>Непройденные вопросы</h1>
          <p className="tw-lead" style={{ marginTop: 8 }}>
            Выберите сотрудника: система покажет вопросы, на которые он ещё не отправлял ответ. Из отмеченных вопросов можно сразу собрать и выдать персональный тест.
          </p>
        </div>

        <div className="tw-card tw-stack-sm">
          <div>
            <label className="tw-label" htmlFor="tw-employee-search">Найти сотрудника</label>
            <div className="tw-search">
              <Icon name="search" />
              <input
                id="tw-employee-search"
                className="tw-field"
                type="text"
                placeholder="Например: Анна или отдел продаж"
                value={employeeQuery}
                onChange={(event) => setEmployeeQuery(event.target.value)}
              />
            </div>
          </div>
          {employeeState === 'loading' && <p className="tw-help" aria-busy="true">Загружаем сотрудников…</p>}
          {employeeState === 'failed' && (
            <div className="tw-row">
              <p className="tw-help">Не получилось загрузить сотрудников.</p>
              <button type="button" className="tw-btn" onClick={() => void loadEmployees()}>Повторить</button>
            </div>
          )}
          {employeeState === 'ready' && (
            <ul className="tw-employee-grid">
              {shownEmployees.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    className={`tw-employee-choice${employeeId === entry.id ? ' tw-employee-choice-on' : ''}`}
                    aria-pressed={employeeId === entry.id}
                    onClick={() => void chooseEmployee(entry)}
                  >
                    <span className="tw-pick-title">{entry.full_name}</span>
                    {employeeDetails(entry) && <span className="tw-pick-text">{employeeDetails(entry)}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {employeeState === 'ready' && employees.length > 0 && shownEmployees.length === 0 && (
            <div className="tw-empty">
              <p className="tw-help">Такого сотрудника не нашли.</p>
              <button type="button" className="tw-btn" onClick={() => setEmployeeQuery('')}>Показать всех</button>
            </div>
          )}
          {employeeState === 'ready' && employees.length === 0 && (
            <p className="tw-help">Нет активных сотрудников, для которых можно собрать тест.</p>
          )}
        </div>

        {employee && questionState === 'loading' && (
          <div className="tw-card tw-empty" aria-busy="true"><p className="tw-help">Собираем историю ответов…</p></div>
        )}
        {employee && questionState === 'failed' && (
          <div className="tw-card tw-empty">
            <Icon name="info" size={44} large />
            <h2>Не получилось загрузить вопросы</h2>
            <p className="tw-help">Проверьте соединение и попробуйте ещё раз.</p>
            <button type="button" className="tw-btn tw-btn-primary" onClick={() => void chooseEmployee(employee)}>Повторить</button>
          </div>
        )}

        {employee && payload && questionState === 'ready' && (
          <>
            <div className="tw-card tw-stack-sm">
              <div className="tw-between tw-row">
                <div>
                  <h2>{employee.full_name}</h2>
                  <p className="tw-help" style={{ marginTop: 4 }}>
                    {`Не пройдено: ${payload.total} · можно назначить: ${payload.available_to_assign} · уже есть в активных назначениях: ${payload.already_assigned}`}
                  </p>
                </div>
                <button type="button" className="tw-btn" onClick={() => void chooseEmployee(employee)}>Обновить</button>
              </div>
              <div className="tw-warn">
                <Icon name="info" />
                <span>Список строится по сохранённым ответам. Одинаковые копии показаны один раз, а вопросы с разными фото или вариантами остаются отдельными.</span>
              </div>
              {payload.total > 0 && (
                <>
                  <div>
                    <label className="tw-label" htmlFor="tw-question-search">Найти вопрос</label>
                    <div className="tw-search">
                      <Icon name="search" />
                      <input
                        id="tw-question-search"
                        className="tw-field"
                        type="text"
                        placeholder="Вопрос, исходный тест или категория"
                        value={questionQuery}
                        onChange={(event) => {
                          setQuestionQuery(event.target.value);
                          setVisibleCount(PAGE_SIZE);
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="tw-label" htmlFor="tw-unanswered-title">Название нового теста</label>
                    <input
                      id="tw-unanswered-title"
                      className="tw-field"
                      type="text"
                      maxLength={255}
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                    />
                  </div>
                  {assignableFiltered.length > 0 && (
                    <div className="tw-row tw-btn-row">
                      <button type="button" className="tw-btn" onClick={toggleAllFiltered}>
                        {allFilteredSelected ? 'Снять выбор с найденных' : `Выбрать найденные (${assignableFiltered.length})`}
                      </button>
                      {selected.length > 0 && (
                        <button type="button" className="tw-btn tw-btn-quiet" onClick={() => setSelected([])}>Снять весь выбор</button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {payload.total === 0 ? (
              <div className="tw-card tw-empty">
                <Icon name="check" size={44} large />
                <h2>Все вопросы уже встречались сотруднику</h2>
                <p className="tw-help">По сохранённым ответам непройденных вопросов не осталось.</p>
              </div>
            ) : filteredQuestions.length === 0 ? (
              <div className="tw-card tw-empty">
                <Icon name="search" size={44} large />
                <h2>Вопросы не найдены</h2>
                <p className="tw-help">Измените поисковый запрос или покажите весь список.</p>
                <button type="button" className="tw-btn" onClick={() => setQuestionQuery('')}>Показать все</button>
              </div>
            ) : (
              <ul className="tw-qlist">
                {visibleQuestions.map((question) => {
                  const checked = selectedSet.has(question.question_id);
                  const meta = [
                    question.source_test_title,
                    question.source_test_subgroup,
                    question.block ? BLOCK_LABEL[question.block] : null,
                  ].filter(Boolean).join(' · ');
                  return (
                    <li key={question.question_id}>
                      <label className={`tw-pick${checked ? ' tw-pick-on' : ''}${question.already_assigned ? ' tw-pick-off' : ''}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={question.already_assigned || busy}
                          onChange={() => toggle(question)}
                        />
                        <span className="tw-pick-body">
                          <span className="tw-pick-title">{question.text}</span>
                          {meta && <span className="tw-pick-text">{meta}</span>}
                          {question.already_assigned && (
                            <>
                              <span className="tw-pill tw-pill-draft">Уже назначен</span>
                              {question.active_assignment_titles.length > 0 && (
                                <span className="tw-pick-text">{question.active_assignment_titles.join(', ')}</span>
                              )}
                            </>
                          )}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}

            {visibleCount < filteredQuestions.length && (
              <div className="tw-row tw-btn-row" style={{ justifyContent: 'center' }}>
                <button type="button" className="tw-btn" onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>
                  Показать ещё
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </WorkspaceShell>
  );
};

export default withAdminOnly(UnansweredQuestionsPage);
